import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { Metodosui } from '../../services/metodosui';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-verimagen',
  templateUrl: './verimagen.component.html',
  styleUrls: ['./verimagen.component.scss'],
  standalone: false
})
export class VerimagenComponent implements OnInit {

  @Input() images: any[] = [];
  @Input() startIndex: number = 0;
  
  // Legacy inputs for backward compatibility
  @Input() image: string = '';
  @Input() title: string = 'Vista Previa';
  @Input() enableCrop: boolean = false;

  @ViewChild('cropCanvas', { static: false }) cropCanvasEl!: ElementRef<HTMLCanvasElement>;

  currentIndex: number = 0;
  isImageLoading: boolean = true;

  // Cropping variables
  isCroppingMode: boolean = false;
  isDocumentMode: boolean = false;
  corners: { x: number; y: number }[] = [];
  activeCornerIndex: number = -1;
  loadingCrop: boolean = false;
  cropImg = new Image();
  
  cropX: number = 0;
  cropY: number = 0;
  cropW: number = 0;
  cropH: number = 0;
  isDraggingCrop: boolean = false;
  rotationAngle: number = 0;
  private cropStartX: number = 0;
  private cropStartY: number = 0;

  @Input() startInCropMode: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosUi: Metodosui
  ) { }

  ngOnInit() {
    // Si no recibimos el array de imágenes pero sí una imagen individual (flujo antiguo)
    if ((!this.images || this.images.length === 0) && this.image) {
      let finalPath = this.image;
      // Validar si es un base64 puro sin prefijo (causante del error 431)
      if (this.image.length > 100 && !this.image.startsWith('data:image') && !this.image.startsWith('http')) {
        finalPath = 'data:image/jpeg;base64,' + this.image;
      }

      this.images = [{
        path: finalPath,
        source: 'legacy',
        texto: this.title
      }];
      this.currentIndex = 0;
    } else {
      this.currentIndex = this.startIndex;
    }

    if (this.startInCropMode) {
      setTimeout(() => {
        this.startCropping(new Event('init'));
      }, 350);
    }
  }

  zoomScale: number = 1;

  get currentImage() {
    return this.images[this.currentIndex];
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  zoomIn(ev: Event) {
    ev.stopPropagation();
    if (this.zoomScale < 3) {
      this.zoomScale = Math.min(3, this.zoomScale + 0.25);
    }
  }

  zoomOut(ev: Event) {
    ev.stopPropagation();
    if (this.zoomScale > 1) {
      this.zoomScale = Math.max(1, this.zoomScale - 0.25);
    }
  }

  panX: number = 0;
  panY: number = 0;
  private lastTouchX: number = 0;
  private lastTouchY: number = 0;
  private isPanning: boolean = false;

  resetZoom(ev?: Event) {
    if (ev) ev.stopPropagation();
    this.zoomScale = 1;
    this.panX = 0;
    this.panY = 0;
  }

  // Touch Gestures for Pinch-to-Zoom & Panning on Mobile
  private initialDistance: number = 0;
  private initialScale: number = 1;
  private isPinching: boolean = false;

  onTouchStart(ev: TouchEvent) {
    if (ev.touches.length === 2) {
      this.isPinching = true;
      this.isPanning = false;
      const t1 = ev.touches[0];
      const t2 = ev.touches[1];
      this.initialDistance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      this.initialScale = this.zoomScale;
    } else if (ev.touches.length === 1 && this.zoomScale > 1) {
      this.isPanning = true;
      this.isPinching = false;
      const touch = ev.touches[0];
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
    }
  }

  onTouchMove(ev: TouchEvent) {
    if (this.isPinching && ev.touches.length === 2) {
      // Prevent browser bouncing
      if (ev.cancelable) {
        ev.preventDefault();
      }
      const t1 = ev.touches[0];
      const t2 = ev.touches[1];
      const currentDistance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      
      if (this.initialDistance > 0) {
        const ratio = currentDistance / this.initialDistance;
        const targetScale = this.initialScale * ratio;
        this.zoomScale = Math.max(1, Math.min(3, parseFloat(targetScale.toFixed(2))));
        if (this.zoomScale === 1) {
          this.panX = 0;
          this.panY = 0;
        }
      }
    } else if (this.isPanning && ev.touches.length === 1 && this.zoomScale > 1) {
      if (ev.cancelable) {
        ev.preventDefault();
      }
      const touch = ev.touches[0];
      const deltaX = touch.clientX - this.lastTouchX;
      const deltaY = touch.clientY - this.lastTouchY;
      
      this.panX += deltaX / this.zoomScale;
      this.panY += deltaY / this.zoomScale;
      
      // Limit panning bounds to keep image visible
      const maxPanX = 120 * (this.zoomScale - 0.8);
      const maxPanY = 160 * (this.zoomScale - 0.8);
      this.panX = Math.max(-maxPanX, Math.min(maxPanX, this.panX));
      this.panY = Math.max(-maxPanY, Math.min(maxPanY, this.panY));
      
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
    }
  }

  onTouchEnd(ev: TouchEvent) {
    if (this.isPinching) {
      this.isPinching = false;
      this.initialDistance = 0;
    }
    if (this.isPanning) {
      this.isPanning = false;
    }
  }

  next(ev: Event) {
    ev.stopPropagation();
    if (this.currentIndex < this.images.length - 1) {
      this.isImageLoading = true;
      this.currentIndex++;
      this.resetZoom();
    }
  }

  prev(ev: Event) {
    ev.stopPropagation();
    if (this.currentIndex > 0) {
      this.isImageLoading = true;
      this.currentIndex--;
      this.resetZoom();
    }
  }

  /**
   * Resuelve el path base para obtener la imagen según su origen.
   * @param source - 'galeria' | 'inspeccion' | 'medida'
   */
  getImagePath(source: string): string {
    switch (source) {
      case 'inspeccion': return 'path_img_inspecciones';
      case 'medida': return 'path_img_medidas';
      case 'factura': return 'path_img_facturas';
      default: return 'path_img_trabajos';
    }
  }

  async startCropping(ev: Event) {
    ev.stopPropagation();
    if (!this.currentImage) return;

    if (this.currentImage.path.startsWith('data:image') || this.currentImage.source === 'legacy') {
      this.isCroppingMode = true;
      this.isDocumentMode = true;
      this.corners = [];
      this.cropX = 0;
      this.cropY = 0;
      this.cropW = 0;
      this.cropH = 0;
      this.cropStartX = 0;
      this.cropStartY = 0;
      this.rotationAngle = 0;
      setTimeout(() => this.initCropCanvas(this.currentImage.path), 200);
      return;
    }

    await this.metodosUi.showLoading('Cargando editor...');
    const configKey = this.getImagePath(this.currentImage.source);
    
    this.javapiService.getImage(this.currentImage.path, configKey).subscribe({
      next: async (base64) => {
        await this.metodosUi.hideLoading();
        if (base64) {
          const base64Data = base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
          this.isCroppingMode = true;
          this.isDocumentMode = true;
          this.corners = [];
          this.cropX = 0;
          this.cropY = 0;
          this.cropW = 0;
          this.cropH = 0;
          this.cropStartX = 0;
          this.cropStartY = 0;
          this.rotationAngle = 0;
          setTimeout(() => this.initCropCanvas(base64Data), 200);
        } else {
          this.metodosUi.showToast('No se pudo descargar la imagen original', 'danger');
        }
      },
      error: async (err) => {
        await this.metodosUi.hideLoading();
        console.error(err);
        this.metodosUi.showToast('Error al descargar la imagen', 'danger');
      }
    });
  }

  initCropCanvas(base64: string) {
    this.cropImg = new Image();
    this.cropImg.onload = () => {
      const canvas = this.cropCanvasEl.nativeElement;
      
      const parent = canvas.parentElement!;
      const maxW = (parent.clientWidth || window.innerWidth) - 24;
      const maxH = (parent.clientHeight || window.innerHeight) - 20;
      
      const ratio = Math.min(maxW / this.cropImg.width, maxH / this.cropImg.height);
      canvas.width = Math.round(this.cropImg.width * ratio);
      canvas.height = Math.round(this.cropImg.height * ratio);
      canvas.style.width = `${canvas.width}px`;
      canvas.style.height = `${canvas.height}px`;
      
      this.redrawCropCanvas();
      if (this.isDocumentMode) {
        this.detectDocumentEdges();
      }
      
      // Canvas Event Listeners for dragging/drawing selection
      canvas.addEventListener('mousedown', this.handleCropStart.bind(this));
      canvas.addEventListener('mousemove', this.handleCropMove.bind(this));
      canvas.addEventListener('mouseup', this.handleCropEnd.bind(this));
      
      canvas.addEventListener('touchstart', this.handleCropStart.bind(this), { passive: false });
      canvas.addEventListener('touchmove', this.handleCropMove.bind(this), { passive: false });
      canvas.addEventListener('touchend', this.handleCropEnd.bind(this));
    };
    this.cropImg.src = base64;
  }

  getEventCoords(ev: any) {
    const canvas = this.cropCanvasEl.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX = ev.clientX;
    let clientY = ev.clientY;
    
    if (ev.touches && ev.touches.length > 0) {
      clientX = ev.touches[0].clientX;
      clientY = ev.touches[0].clientY;
    } else if (ev.changedTouches && ev.changedTouches.length > 0) {
      clientX = ev.changedTouches[0].clientX;
      clientY = ev.changedTouches[0].clientY;
    }
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }

  handleCropStart(ev: any) {
    if (ev.cancelable) {
      ev.preventDefault();
    }
    const pos = this.getEventCoords(ev);
    
    if (this.isDocumentMode) {
      let minD = 45; // Umbral táctil ampliado para facilitar agarre en móviles
      this.activeCornerIndex = -1;
      for (let i = 0; i < this.corners.length; i++) {
        const dx = pos.x - this.corners[i].x;
        const dy = pos.y - this.corners[i].y;
        const d = Math.hypot(dx, dy);
        if (d < minD) {
          minD = d;
          this.activeCornerIndex = i;
        }
      }
      if (this.activeCornerIndex >= 0) {
        this.isDraggingCrop = true;
      }
      return;
    }
    
    this.cropStartX = pos.x;
    this.cropStartY = pos.y;
    this.cropX = pos.x;
    this.cropY = pos.y;
    this.cropW = 0;
    this.cropH = 0;
    this.isDraggingCrop = true;
    this.redrawCropCanvas();
  }

  handleCropMove(ev: any) {
    if (ev.cancelable) {
      ev.preventDefault();
    }
    if (!this.isDraggingCrop) return;
    const pos = this.getEventCoords(ev);
    
    if (this.isDocumentMode) {
      if (this.activeCornerIndex >= 0) {
        const canvas = this.cropCanvasEl.nativeElement;
        this.corners[this.activeCornerIndex].x = Math.max(0, Math.min(canvas.width, pos.x));
        this.corners[this.activeCornerIndex].y = Math.max(0, Math.min(canvas.height, pos.y));
        this.redrawCropCanvas();
      }
      return;
    }
    
    this.cropW = pos.x - this.cropStartX;
    this.cropH = pos.y - this.cropStartY;
    this.redrawCropCanvas();
  }

  handleCropEnd(ev: any) {
    if (ev && ev.cancelable) {
      ev.preventDefault();
    }
    this.isDraggingCrop = false;
    this.activeCornerIndex = -1;
  }

  rotate() {
    this.rotationAngle = (this.rotationAngle + 90) % 360;
    const canvas = this.cropCanvasEl.nativeElement;
    
    const parent = canvas.parentElement!;
    const maxW = (parent.clientWidth || window.innerWidth) - 24;
    const maxH = (parent.clientHeight || window.innerHeight) - 20;
    
    const isRotated = (this.rotationAngle === 90 || this.rotationAngle === 270);
    const imgW = isRotated ? this.cropImg.height : this.cropImg.width;
    const imgH = isRotated ? this.cropImg.width : this.cropImg.height;
    
    const ratio = Math.min(maxW / imgW, maxH / imgH);
    canvas.width = Math.round(imgW * ratio);
    canvas.height = Math.round(imgH * ratio);
    canvas.style.width = `${canvas.width}px`;
    canvas.style.height = `${canvas.height}px`;
    
    this.cropX = 0;
    this.cropY = 0;
    this.cropW = 0;
    this.cropH = 0;
    this.cropStartX = 0;
    this.cropStartY = 0;
    
    if (this.isDocumentMode) {
      this.detectDocumentEdges();
    } else {
      this.redrawCropCanvas();
    }
  }

  toggleCropMode(ev: any) {
    this.isDocumentMode = ev.detail.value === 'document';
    if (this.isDocumentMode && this.corners.length === 0) {
      this.detectDocumentEdges();
    } else {
      this.redrawCropCanvas();
    }
  }

  detectDocumentEdges() {
    const canvas = this.cropCanvasEl.nativeElement;
    const w = canvas.width;
    const h = canvas.height;
    
    const defaultCorners = [
      { x: w * 0.1, y: h * 0.1 },
      { x: w * 0.9, y: h * 0.1 },
      { x: w * 0.9, y: h * 0.9 },
      { x: w * 0.1, y: h * 0.9 }
    ];
    
    try {
      const ctx = canvas.getContext('2d')!;
      
      // Dibujar imagen limpia temporalmente para escanear los colores reales
      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate((this.rotationAngle * Math.PI) / 180);
      const isRotated = (this.rotationAngle === 90 || this.rotationAngle === 270);
      const drawW = isRotated ? h : w;
      const drawH = isRotated ? w : h;
      ctx.drawImage(this.cropImg, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
      
      const imgData = ctx.getImageData(0, 0, w, h);
      const data = imgData.data;
      
      // Calcular luminosidad media para umbral dinámico adaptativo
      let totalBrightness = 0;
      let brightnessCount = 0;
      const step = 8;
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const idx = (y * w + x) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          totalBrightness += 0.299 * r + 0.587 * g + 0.114 * b;
          brightnessCount++;
        }
      }
      const avgBrightness = brightnessCount > 0 ? (totalBrightness / brightnessCount) : 120;
      const threshold = Math.max(90, Math.min(185, avgBrightness + 25)); // Umbral adaptativo a la luz de la foto
      
      let firstY = h, lastY = 0;
      let firstX = w, lastX = 0;
      
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const idx = (y * w + x) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
          
          if (brightness > threshold) {
            if (y < firstY) firstY = y;
            if (y > lastY) lastY = y;
            if (x < firstX) firstX = x;
            if (x > lastX) lastX = x;
          }
        }
      }
      
      if (lastX - firstX > w * 0.2 && lastY - firstY > h * 0.2) {
        const pad = 10;
        const x1 = Math.max(0, firstX - pad);
        const y1 = Math.max(0, firstY - pad);
        const x2 = Math.min(w, lastX + pad);
        const y2 = Math.min(h, lastY + pad);
        
        this.corners = [
          { x: x1, y: y1 },
          { x: x2, y: y1 },
          { x: x2, y: y2 },
          { x: x1, y: y2 }
        ];
        this.redrawCropCanvas();
        return;
      }
    } catch (e) {
      console.warn('Error en detección automática de bordes, usando defaults', e);
    }
    
    this.corners = defaultCorners;
    this.redrawCropCanvas();
  }

  resetToFullFrame() {
    const canvas = this.cropCanvasEl.nativeElement;
    const w = canvas.width;
    const h = canvas.height;
    this.corners = [
      { x: 0, y: 0 },
      { x: w, y: 0 },
      { x: w, y: h },
      { x: 0, y: h }
    ];
    this.redrawCropCanvas();
  }

  redrawCropCanvas() {
    const canvas = this.cropCanvasEl.nativeElement;
    const ctx = canvas.getContext('2d')!;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((this.rotationAngle * Math.PI) / 180);
    
    const isRotated = (this.rotationAngle === 90 || this.rotationAngle === 270);
    const origW = isRotated ? this.cropImg.height : this.cropImg.width;
    const origH = isRotated ? this.cropImg.width : this.cropImg.height;

    const imgAspect = origW / origH;
    const canvasAspect = canvas.width / canvas.height;

    let drawW: number;
    let drawH: number;

    if (imgAspect > canvasAspect) {
      drawW = canvas.width;
      drawH = canvas.width / imgAspect;
    } else {
      drawH = canvas.height;
      drawW = canvas.height * imgAspect;
    }

    const drawImgW = isRotated ? drawH : drawW;
    const drawImgH = isRotated ? drawW : drawH;

    ctx.drawImage(this.cropImg, -drawImgW / 2, -drawImgH / 2, drawImgW, drawImgH);
    ctx.restore();
    
    if (this.isDocumentMode && this.corners.length === 4) {
      // Máscara oscura exterior
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.moveTo(this.corners[0].x, this.corners[0].y);
      ctx.lineTo(this.corners[3].x, this.corners[3].y);
      ctx.lineTo(this.corners[2].x, this.corners[2].y);
      ctx.lineTo(this.corners[1].x, this.corners[1].y);
      ctx.closePath();
      ctx.fill('evenodd');
      
      // Dibujar líneas de conexión del cuadrilátero
      ctx.strokeStyle = '#3880ff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(this.corners[0].x, this.corners[0].y);
      ctx.lineTo(this.corners[1].x, this.corners[1].y);
      ctx.lineTo(this.corners[2].x, this.corners[2].y);
      ctx.lineTo(this.corners[3].x, this.corners[3].y);
      ctx.closePath();
      ctx.stroke();
      
      // Dibujar los controladores interactivos en las esquinas (estilo azul nativo con borde blanco)
      this.corners.forEach((c) => {
        ctx.fillStyle = '#2563eb';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(c.x, c.y, 14, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      });
      return;
    }
    
    const x = Math.min(this.cropStartX, this.cropStartX + this.cropW);
    const y = Math.min(this.cropStartY, this.cropStartY + this.cropH);
    const w = Math.abs(this.cropW);
    const h = Math.abs(this.cropH);
    
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    if (w > 10 && h > 10) {
      ctx.fillRect(0, 0, canvas.width, y);
      ctx.fillRect(0, y + h, canvas.width, canvas.height - (y + h));
      ctx.fillRect(0, y, x, h);
      ctx.fillRect(x + w, y, canvas.width - (x + w), h);
      
      ctx.strokeStyle = '#3880ff';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
      
      ctx.fillStyle = '#3880ff';
      const size = 6;
      ctx.fillRect(x - size/2, y - size/2, size, size);
      ctx.fillRect(x + w - size/2, y - size/2, size, size);
      ctx.fillRect(x - size/2, y + h - size/2, size, size);
      ctx.fillRect(x + w - size/2, y + h - size/2, size, size);
    } else {
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }

  cancelCropping(ev?: Event) {
    if (ev) {
      ev.stopPropagation();
      ev.preventDefault();
    }
    this.isCroppingMode = false;
  }

  async confirmCropping() {
    const canvas = this.cropCanvasEl.nativeElement;
    
    if (this.isDocumentMode) {
      if (this.corners.length !== 4) {
        this.metodosUi.showToast('Configura las 4 esquinas del documento', 'warning');
        return;
      }
      
      this.loadingCrop = true;
      await this.metodosUi.showLoading('Guardando cambios...');
      
      try {
        // 1. Crear canvas de origen con la rotación actual aplicada
        const rotCanvas = document.createElement('canvas');
        const isRotated = (this.rotationAngle === 90 || this.rotationAngle === 270);
        rotCanvas.width = isRotated ? this.cropImg.height : this.cropImg.width;
        rotCanvas.height = isRotated ? this.cropImg.width : this.cropImg.height;

        const rotCtx = rotCanvas.getContext('2d')!;
        rotCtx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
        rotCtx.rotate((this.rotationAngle * Math.PI) / 180);
        rotCtx.drawImage(this.cropImg, -this.cropImg.width / 2, -this.cropImg.height / 2);

        // 2. Mapear las 4 esquinas del canvas visible al canvas rotado original
        const scaleX = rotCanvas.width / canvas.width;
        const scaleY = rotCanvas.height / canvas.height;

        const pts = this.corners.map(c => ({
          x: c.x * scaleX,
          y: c.y * scaleY
        }));

        const w1 = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
        const w2 = Math.hypot(pts[2].x - pts[3].x, pts[2].y - pts[3].y);
        const destW = Math.round(Math.max(w1, w2));

        const h1 = Math.hypot(pts[3].y - pts[0].y, pts[3].x - pts[0].x);
        const h2 = Math.hypot(pts[2].y - pts[1].y, pts[2].x - pts[1].x);
        const destH = Math.round(Math.max(h1, h2));

        const offCanvas = this.warpPerspective(rotCanvas, pts, destW, destH);
        const croppedBase64 = offCanvas.toDataURL('image/jpeg', 0.95);
        
        if (this.currentImage.path.startsWith('data:image') || this.currentImage.source === 'legacy' || this.currentImage.source !== 'factura') {
          await this.metodosUi.hideLoading();
          this.metodosUi.showToast('Imagen procesada correctamente', 'success');
          this.isCroppingMode = false;
          this.modalCtrl.dismiss({ refresh: true, croppedBase64: croppedBase64, imageItem: this.currentImage });
          return;
        }

        const userJson = localStorage.getItem('user');
        const username = userJson ? JSON.parse(userJson).l_usuario || 'system' : 'system';
        
        await firstValueFrom(this.javapiService.overwriteFactura(croppedBase64, this.currentImage.path, username));
        
        await this.metodosUi.hideLoading();
        this.metodosUi.showToast('Imagen guardada con éxito', 'success');
        this.isCroppingMode = false;
        this.modalCtrl.dismiss({ refresh: true, croppedBase64: croppedBase64, imageItem: this.currentImage });
      } catch (e) {
        await this.metodosUi.hideLoading();
        console.error(e);
        this.metodosUi.showToast('Error al guardar los cambios en el servidor', 'danger');
      } finally {
        this.loadingCrop = false;
      }
      return;
    }

    const x = Math.min(this.cropStartX, this.cropStartX + this.cropW);
    const y = Math.min(this.cropStartY, this.cropStartY + this.cropH);
    const w = Math.abs(this.cropW);
    const h = Math.abs(this.cropH);
    
    let isOnlyRotation = false;
    let finalX = x;
    let finalY = y;
    let finalW = w;
    let finalH = h;
    
    if (w < 15 || h < 15) {
      if (this.rotationAngle !== 0) {
        isOnlyRotation = true;
        finalX = 0;
        finalY = 0;
        finalW = canvas.width;
        finalH = canvas.height;
      } else {
        this.metodosUi.showToast('Arrastra en la pantalla para seleccionar un área de recorte', 'warning');
        return;
      }
    }
    
    this.loadingCrop = true;
    await this.metodosUi.showLoading('Guardando cambios...');
    
    try {
      const scaleX = this.cropImg.width / canvas.width;
      const scaleY = this.cropImg.height / canvas.height;
      
      const origX = finalX * scaleX;
      const origY = finalY * scaleY;
      const origW = finalW * scaleX;
      const origH = finalH * scaleY;
      
      const rotCanvas = document.createElement('canvas');
      const isRotated = (this.rotationAngle === 90 || this.rotationAngle === 270);
      rotCanvas.width = isRotated ? this.cropImg.height : this.cropImg.width;
      rotCanvas.height = isRotated ? this.cropImg.width : this.cropImg.height;
      
      const rotCtx = rotCanvas.getContext('2d')!;
      rotCtx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
      rotCtx.rotate((this.rotationAngle * Math.PI) / 180);
      rotCtx.drawImage(this.cropImg, -this.cropImg.width / 2, -this.cropImg.height / 2);
      
      const offCanvas = document.createElement('canvas');
      offCanvas.width = origW;
      offCanvas.height = origH;
      const offCtx = offCanvas.getContext('2d')!;
      
      offCtx.drawImage(rotCanvas, origX, origY, origW, origH, 0, 0, origW, origH);
      const croppedBase64 = offCanvas.toDataURL('image/jpeg', 0.95);
      
      if (this.currentImage.path.startsWith('data:image') || this.currentImage.source === 'legacy' || this.currentImage.source !== 'factura') {
        await this.metodosUi.hideLoading();
        this.metodosUi.showToast('Imagen procesada correctamente', 'success');
        this.isCroppingMode = false;
        this.modalCtrl.dismiss({ refresh: true, croppedBase64: croppedBase64, imageItem: this.currentImage });
        return;
      }

      const userJson = localStorage.getItem('user');
      const username = userJson ? JSON.parse(userJson).l_usuario || 'system' : 'system';
      
      await firstValueFrom(this.javapiService.overwriteFactura(croppedBase64, this.currentImage.path, username));
      
      await this.metodosUi.hideLoading();
      this.metodosUi.showToast('Imagen guardada con éxito', 'success');
      this.isCroppingMode = false;
      this.modalCtrl.dismiss({ refresh: true, croppedBase64: croppedBase64, imageItem: this.currentImage });
    } catch (e) {
      await this.metodosUi.hideLoading();
      console.error(e);
      this.metodosUi.showToast('Error al guardar los cambios en el servidor', 'danger');
    } finally {
      this.loadingCrop = false;
    }
  }

  private warpPerspective(srcCanvas: HTMLCanvasElement, srcPts: any[], destW: number, destH: number): HTMLCanvasElement {
    const destCanvas = document.createElement('canvas');
    destCanvas.width = destW;
    destCanvas.height = destH;
    const destCtx = destCanvas.getContext('2d')!;

    const srcCtx = srcCanvas.getContext('2d')!;
    const srcImageData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
    const srcData = srcImageData.data;
    const srcW = srcCanvas.width;
    const srcH = srcCanvas.height;

    const destImageData = destCtx.createImageData(destW, destH);
    const destData = destImageData.data;

    // Puntos cuadriláteros origen: P0=TL, P1=TR, P2=BR, P3=BL
    const x0 = srcPts[0].x, y0 = srcPts[0].y;
    const x1 = srcPts[1].x, y1 = srcPts[1].y;
    const x2 = srcPts[2].x, y2 = srcPts[2].y;
    const x3 = srcPts[3].x, y3 = srcPts[3].y;

    const dx1 = x1 - x2;
    const dx2 = x3 - x2;
    const sx = x0 - x1 + x2 - x3;

    const dy1 = y1 - y2;
    const dy2 = y3 - y2;
    const sy = y0 - y1 + y2 - y3;

    let g = 0, h = 0;
    const den = dx1 * dy2 - dy1 * dx2;
    if (Math.abs(den) > 0.000001) {
      g = (sx * dy2 - sy * dx2) / den;
      h = (dx1 * sy - dy1 * sx) / den;
    }

    const c00 = x0;
    const c10 = x1 - x0 + g * x1;
    const c01 = x3 - x0 + h * x3;

    const d00 = y0;
    const d10 = y1 - y0 + g * y1;
    const d01 = y3 - y0 + h * y3;

    let destIdx = 0;
    for (let y = 0; y < destH; y++) {
      const v = y / destH;
      for (let x = 0; x < destW; x++) {
        const u = x / destW;
        const denom = 1 + g * u + h * v;
        const srcX = (c00 + c10 * u + c01 * v) / denom;
        const srcY = (d00 + d10 * u + d01 * v) / denom;

        if (srcX >= 0 && srcX < srcW && srcY >= 0 && srcY < srcH) {
          const xFloor = Math.floor(srcX);
          const yFloor = Math.floor(srcY);
          const xCeil = Math.min(xFloor + 1, srcW - 1);
          const yCeil = Math.min(yFloor + 1, srcH - 1);

          const fx = srcX - xFloor;
          const fy = srcY - yFloor;
          const w00 = (1 - fx) * (1 - fy);
          const w10 = fx * (1 - fy);
          const w01 = (1 - fx) * fy;
          const w11 = fx * fy;

          const idx00 = (yFloor * srcW + xFloor) * 4;
          const idx10 = (yFloor * srcW + xCeil) * 4;
          const idx01 = (yCeil * srcW + xFloor) * 4;
          const idx11 = (yCeil * srcW + xCeil) * 4;

          destData[destIdx] = (srcData[idx00] * w00 + srcData[idx10] * w10 + srcData[idx01] * w01 + srcData[idx11] * w11);
          destData[destIdx + 1] = (srcData[idx00 + 1] * w00 + srcData[idx10 + 1] * w10 + srcData[idx01 + 1] * w01 + srcData[idx11 + 1] * w11);
          destData[destIdx + 2] = (srcData[idx00 + 2] * w00 + srcData[idx10 + 2] * w10 + srcData[idx01 + 2] * w01 + srcData[idx11 + 2] * w11);
          destData[destIdx + 3] = 255;
        } else {
          destData[destIdx + 3] = 0;
        }
        destIdx += 4;
      }
    }

    destCtx.putImageData(destImageData, 0, 0);
    return destCanvas;
  }
}
